import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-6" data-testid="booking-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md text-center"
      >
        {status === 'checking' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <Loader2 className="h-16 w-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-3xl font-light mb-2">Confirming Payment</h1>
            <p className="text-muted-foreground">Please wait while we verify your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-light mb-2">Booking Confirmed!</h1>
            <p className="text-muted-foreground mb-8">
              Thank you for your booking. We've sent a confirmation to your email address.
            </p>
            <div className="space-y-3">
              <Button
                className="w-full rounded-full"
                onClick={() => navigate('/dashboard')}
                data-testid="view-appointments-btn"
              >
                View My Appointments
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => navigate('/intake-form')}
                data-testid="complete-intake-btn"
              >
                Complete Intake Form
              </Button>
            </div>
          </div>
        )}

        {status === 'expired' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <h1 className="font-heading text-3xl font-light mb-2">Session Expired</h1>
            <p className="text-muted-foreground mb-8">
              Your payment session has expired. Please try booking again.
            </p>
            <Button
              className="w-full rounded-full"
              onClick={() => navigate('/book')}
              data-testid="try-again-btn"
            >
              Book Again
            </Button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <h1 className="font-heading text-3xl font-light mb-2">Payment Status Unknown</h1>
            <p className="text-muted-foreground mb-8">
              We couldn't verify your payment status. Please check your email for confirmation 
              or contact us if you need assistance.
            </p>
            <div className="space-y-3">
              <Button
                className="w-full rounded-full"
                onClick={() => navigate('/dashboard')}
                data-testid="check-dashboard-btn"
              >
                Check My Appointments
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default BookingSuccessPage;
